import type { KotDto, KotPrintStatus, KotPrintSummary } from '../api/types/dine.types';

/*
 * The print chip on a ticket, and the "did not print" alert (Print module
 * contract §6–7, D-P10). The waiter only sees how far the paper got; reprint
 * is a kitchen / manager action and has no button here.
 */

export interface PrintChipView {
  label: string;
  failed: boolean;
}

const LABELS: Partial<Record<KotPrintStatus, string>> = {
  QUEUED: 'Queued',
  PRINTING: 'Printing',
  SENT: 'Sent',
  PRINTED: 'Printed',
  FAILED: 'Failed',
  HANDLED: 'Handled',
};

/**
 * What the chip says, or null for no chip: a BROWSER-mode ticket (no printer,
 * the kitchen screen has it), a ticket whose jobs were all withdrawn, or an
 * API that sent no `print`. A ticket that went to the backup names it.
 */
export function printChip(print: KotPrintSummary | null | undefined): PrintChipView | null {
  if (!print || print.mode !== 'AGENT' || !print.status) return null;
  const label = LABELS[print.status];
  if (!label) return null;
  if (print.status === 'FAILED') return { label, failed: true };
  if (print.rerouted && print.printerName) {
    const at =
      print.status === 'PRINTED'
        ? `Printed at ${print.printerName}`
        : print.status === 'SENT'
          ? `Sent to ${print.printerName}`
          : print.status === 'HANDLED'
            ? label
            : `${label} at ${print.printerName}`;
    return { label: at, failed: false };
  }
  return { label, failed: false };
}

/** "KOT-000123 did not print at Main kitchen. The kitchen screen has it." */
export function printFailedMessage(kot: Pick<KotDto, 'kotNumber' | 'print'>): string {
  const at = kot.print?.printerName ? ` at ${kot.print.printerName}` : '';
  return `${kot.kotNumber} did not print${at}. The kitchen screen has it.`;
}

/** Several at once read as one alert. */
export function printFailedAlert(kots: ReadonlyArray<Pick<KotDto, 'kotNumber' | 'print'>>): string | null {
  return kots.length === 0 ? null : kots.map(printFailedMessage).join(' ');
}

function statusOf(k: Pick<KotDto, 'print'>): KotPrintStatus | null {
  return k.print?.mode === 'AGENT' ? k.print.status ?? null : null;
}

/**
 * Decides which tickets raise the "did not print" alert, once per ticket for
 * the session (memory only).
 *
 * - `sent`: the tickets a Send returned. One already FAILED alerts now; all
 *   of them are this waiter's to watch.
 * - `polled`: a later poll. A watched ticket alerts when it turns FAILED and
 *   was not FAILED when last seen. `watch` adds the tickets to the watched set
 *   (an order the waiter opened); the kitchen list only alerts watched ones,
 *   so a waiter is not told about every other waiter's tickets.
 */
export class PrintAlertTracker {
  private readonly last = new Map<string, KotPrintStatus | null>();
  private readonly watched = new Set<string>();
  private readonly shown = new Set<string>();

  sent<K extends Pick<KotDto, 'id' | 'print'>>(kots: ReadonlyArray<K>): K[] {
    const out: K[] = [];
    for (const k of kots) {
      this.watched.add(k.id);
      const now = statusOf(k);
      this.last.set(k.id, now);
      if (now === 'FAILED' && this.take(k.id)) out.push(k);
    }
    return out;
  }

  polled<K extends Pick<KotDto, 'id' | 'print'>>(kots: ReadonlyArray<K>, watch = false): K[] {
    const out: K[] = [];
    for (const k of kots) {
      const known = this.last.has(k.id);
      const before = this.last.get(k.id) ?? null;
      const now = statusOf(k);
      this.last.set(k.id, now);
      if (watch) this.watched.add(k.id);
      if (now !== 'FAILED' || !known || before === 'FAILED' || !this.watched.has(k.id)) continue;
      if (this.take(k.id)) out.push(k);
    }
    return out;
  }

  private take(id: string): boolean {
    if (this.shown.has(id)) return false;
    this.shown.add(id);
    return true;
  }
}
